<template>
  <div class="create-page">
    <!-- Header Section -->
    <div class="page-header">
      <h1>Create New Ski School</h1>
      <p class="page-description">Share your ski school with the community</p>
    </div>

    <div class="create-form-container">
      <div class="create-form">
        <form @submit.prevent="createSkiSchool">
          <div class="form-row">
            <div class="form-group">
              <label for="name">School Name *</label>
              <input 
                type="text" 
                id="name"
                v-model="newSchool.name" 
                required 
                class="form-input"
                placeholder="Enter ski school name"
              />
            </div>
            <div class="form-group">
              <label for="country">Country *</label>
              <input 
                type="text" 
                id="country"
                ref="countryInput"
                v-model="newSchool.country" 
                required 
                class="form-input"
                placeholder="Enter country"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="description">Description *</label>
            <textarea 
              id="description"
              v-model="newSchool.description" 
              required 
              class="form-textarea"
              rows="4"
              placeholder="Describe your ski school and its offerings"
            ></textarea>
          </div>

          <!-- Logo/Profile Picture Upload -->
          <div class="form-group">
            <label for="logo">School Logo/Profile Picture</label>
            <div class="file-upload-container">
              <input 
                type="file" 
                id="logo"
                ref="logoInput"
                @change="handleLogoUpload"
                accept="image/*"
                class="file-input"
              />
              <label for="logo" class="file-upload-button">
                <i class="fas fa-upload"></i>
                Choose Logo Image
              </label>
              <span class="file-name" v-if="newSchool.logoFile">{{ newSchool.logoFile.name }}</span>
            </div>
            <!-- Image Preview -->
            <div v-if="newSchool.logoPreview" class="image-preview-container">
              <img :src="newSchool.logoPreview" alt="Logo Preview" class="image-preview" />
              <button type="button" @click="removeLogoPreview" class="remove-image-button">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <small class="input-help">Recommended: Square image, max 5MB (JPEG, PNG)</small>
          </div>

          <div class="form-group">
            <label for="address">Address</label>
            <input 
              type="text"
              id="address"
              ref="addressInput"
              v-model="newSchool.address" 
              class="form-input"
              placeholder="Enter full address or business name"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="season_start_date">Season Start Date</label>
              <input 
                type="text" 
                id="season_start_date"
                v-model="newSchool.season_start_date" 
                class="form-input"
                placeholder="MM-DD (e.g., 12-01)"
                pattern="^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$"
                maxlength="5"
                title="Enter date in MM-DD format (e.g., 12-01 for December 1st)"
                @input="formatDateInput($event, 'season_start_date')"
              />
              <small class="input-help">Format: MM-DD (e.g., 12-01 for December 1st)</small>
            </div>
            <div class="form-group">
              <label for="season_end_date">Season End Date</label>
              <input 
                type="text" 
                id="season_end_date"
                v-model="newSchool.season_end_date" 
                class="form-input"
                placeholder="MM-DD (e.g., 03-31)"
                pattern="^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$"
                maxlength="5"
                title="Enter date in MM-DD format (e.g., 03-31 for March 31st)"
                @input="formatDateInput($event, 'season_end_date')"
              />
              <small class="input-help">Format: MM-DD (e.g., 03-31 for March 31st)</small>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="price_per_set">Price per Lesson</label>
              <input 
                type="number" 
                id="price_per_set"
                v-model="newSchool.price_per_set" 
                step="0.01"
                min="0"
                class="form-input"
                placeholder="0.00"
              />
            </div>
            <div class="form-group">
              <label for="currency">Currency</label>
              <select id="currency" v-model="newSchool.currency" class="form-select">
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="contact_email">Contact Email</label>
              <input 
                type="email" 
                id="contact_email"
                v-model="newSchool.contact_email" 
                class="form-input"
                placeholder="contact@skischool.com"
              />
            </div>
            <div class="form-group">
              <label for="contact_phone">Contact Phone</label>
              <input 
                type="tel" 
                id="contact_phone"
                v-model="newSchool.contact_phone" 
                class="form-input"
                placeholder="+1234567890"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="website_url">Website URL</label>
            <input 
              type="url" 
              id="website_url"
              v-model="newSchool.website_url" 
              class="form-input"
              placeholder="https://www.skischool.com"
            />
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="instagram_url">Instagram URL</label>
              <input 
                type="url" 
                id="instagram_url"
                v-model="newSchool.instagram_url" 
                class="form-input"
                placeholder="https://instagram.com/skischool"
              />
            </div>
            <div class="form-group">
              <label for="facebook_url">Facebook URL</label>
              <input 
                type="url" 
                id="facebook_url"
                v-model="newSchool.facebook_url" 
                class="form-input"
                placeholder="https://facebook.com/skischool"
              />
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="submit-button" :disabled="creating">
              <i class="fas fa-save"></i>
              {{ creating ? 'Creating...' : 'Create Ski School' }}
            </button>
            <button type="button" @click="cancel" class="cancel-button">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SkiSchoolCreate',
  data() {
    return {
      creating: false,
      isAuthenticated: false,
      user: {},
      newSchool: {
        name: '',
        description: '',
        country: '',
        address: '',
        latitude: '',
        longitude: '',
        season_start_date: '',
        season_end_date: '',
        price_per_set: '',
        currency: 'USD',
        contact_email: '',
        contact_phone: '',
        website_url: '',
        instagram_url: '',
        facebook_url: '',
        logoFile: null,
        logoPreview: null
      }
    }
  },
  async created() {
    await this.checkAuthentication();
    
    // Load Google Maps API
    this.loadGoogleMapsAPI();
  },
  mounted() {
    // Initialize autocomplete if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      this.initializeAutocomplete();
    }
  },
  watch: {
    // Remove the show watcher since we don't need it anymore
  },
  methods: {
    async checkAuthentication() {
      const token = localStorage.getItem("access_token");
      if (token) {
        try {
          const response = await axios.get("https://api.6buoys.com/api/profile/", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.user = response.data;
          this.isAuthenticated = true;
        } catch (error) {
          console.error("Error fetching user data:", error);
          this.isAuthenticated = false;
          // Redirect to login if not authenticated
          this.$router.push('/login');
        }
      } else {
        this.isAuthenticated = false;
        // Redirect to login if not authenticated
        this.$router.push('/login');
      }
    },

    async createSkiSchool() {
      this.creating = true;
      
      try {
        const token = localStorage.getItem("access_token");
        
        // Use FormData for file uploads
        const formData = new FormData();
        
        // Add creator field (required by backend)
        formData.append('creator', this.user.id);
        
        // Set is_active to true for new ski schools
        formData.append('is_active', true);
        
        // Add all form fields
        Object.keys(this.newSchool).forEach(key => {
          const value = this.newSchool[key];
          
          // Skip logoPreview and logoFile special handling
          if (key === 'logoPreview') return;
          if (key === 'logoFile') return;
          
          // Only add non-empty values
          if (value !== '' && value !== null && value !== undefined) {
            // Convert numeric fields properly
            if (key === 'price_per_set' && value !== '') {
              formData.append(key, parseFloat(value));
            }
            // Ensure coordinates are numbers
            else if ((key === 'latitude' || key === 'longitude') && value !== '') {
              formData.append(key, parseFloat(value));
            }
            else {
              formData.append(key, value);
            }
          }
        });
        
        // Add logo file if selected
        if (this.newSchool.logoFile) {
          formData.append('logo', this.newSchool.logoFile);
        }

        console.log('Sending data to backend with FormData');

        const response = await axios.post('https://api.6buoys.com/profiles/ski-schools/create/', formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        console.log('Backend response:', response.data);

        // Reset form
        this.resetForm();
        
        alert('Ski school created successfully!');
        
        // Navigate back to ski schools page
        this.$router.push('/ski-schools');
      } catch (error) {
        console.error('Full error object:', error);
        console.error('Error response:', error.response);
        
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
          console.error('Response headers:', error.response.headers);
          
          // Check if response is HTML (error page)
          if (typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE')) {
            alert('Server error: The backend returned an HTML error page. Check if the backend is running and the endpoint exists.');
          } else if (error.response.data) {
            const errors = Object.values(error.response.data).flat().join('\n');
            alert(`Error creating ski school:\n${errors}`);
          } else {
            alert(`HTTP ${error.response.status}: ${error.response.statusText}`);
          }
        } else if (error.request) {
          console.error('Request was made but no response received:', error.request);
          alert('Network error: Could not connect to the backend. Make sure the server is running.');
        } else {
          console.error('Error message:', error.message);
          alert('Error creating ski school. Please try again.');
        }
      } finally {
        this.creating = false;
      }
    },

    resetForm() {
      this.newSchool = {
        name: '',
        description: '',
        country: '',
        address: '',
        latitude: '',
        longitude: '',
        season_start_date: '',
        season_end_date: '',
        price_per_set: '',
        currency: 'USD',
        contact_email: '',
        contact_phone: '',
        website_url: '',
        instagram_url: '',
        facebook_url: '',
        logoFile: null,
        logoPreview: null
      };
    },

    cancel() {
      this.resetForm();
      this.$router.push('/ski-schools');
    },

    initializeAutocomplete() {
      this.$nextTick(() => {
        // Check if Google Maps API is loaded
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          console.warn('Google Maps API not loaded yet');
          return;
        }

        // Initialize address autocomplete
        if (this.$refs.addressInput) {
          console.log('Initializing address autocomplete');
          const addressAutocomplete = new window.google.maps.places.Autocomplete(
            this.$refs.addressInput,
            {
              types: ['establishment', 'geocode'],
              fields: ['formatted_address', 'geometry', 'address_components', 'name', 'place_id']
            }
          );

          addressAutocomplete.addListener('place_changed', () => {
            console.log('Address place changed');
            const place = addressAutocomplete.getPlace();
            console.log('Selected place:', place);
            
            if (place.geometry) {
              // Use place name if it's a business, otherwise use formatted address
              this.newSchool.address = place.name && place.types?.includes('establishment') 
                ? `${place.name}, ${place.formatted_address}`
                : place.formatted_address;
              
              // Round coordinates to fit backend constraints
              this.newSchool.latitude = parseFloat(place.geometry.location.lat().toFixed(8));
              this.newSchool.longitude = parseFloat(place.geometry.location.lng().toFixed(8));
              
              console.log('Updated address:', this.newSchool.address);
              console.log('Coordinates:', this.newSchool.latitude, this.newSchool.longitude);
              
              // Extract country from address components if not already set
              if (!this.newSchool.country && place.address_components) {
                const countryComponent = place.address_components.find(
                  component => component.types.includes('country')
                );
                if (countryComponent) {
                  this.newSchool.country = countryComponent.long_name;
                  console.log('Auto-filled country:', this.newSchool.country);
                }
              }
              // Trigger Vue reactivity
              this.$forceUpdate();
            }
          });

          // Prevent form submission when Enter is pressed in autocomplete
          this.$refs.addressInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          });
        } else {
          console.log('Address input ref not found');
        }

        // Initialize country autocomplete
        if (this.$refs.countryInput) {
          const countryAutocomplete = new window.google.maps.places.Autocomplete(
            this.$refs.countryInput,
            {
              types: ['(regions)'],
              fields: ['formatted_address', 'geometry', 'address_components']
            }
          );

          countryAutocomplete.addListener('place_changed', () => {
            const place = countryAutocomplete.getPlace();
            if (place.address_components) {
              const countryComponent = place.address_components.find(
                component => component.types.includes('country')
              );
              if (countryComponent) {
                this.newSchool.country = countryComponent.long_name;
                // Trigger Vue reactivity
                this.$forceUpdate();
              }
            }
          });

          // Prevent form submission when Enter is pressed in autocomplete
          this.$refs.countryInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          });
        }
      });
    },

    formatDateInput(event, field) {
      let value = event.target.value.replace(/\D/g, ''); // Remove non-digits
      
      if (value.length >= 2) {
        value = value.substring(0, 2) + '-' + value.substring(2, 4);
      }
      
      this.newSchool[field] = value;
    },

    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.newSchool.logoPreview = e.target.result;
          this.newSchool.logoFile = file;
        };
        reader.readAsDataURL(file);
      }
    },

    removeLogoPreview() {
      this.newSchool.logoPreview = null;
      this.newSchool.logoFile = null;
    },

    loadGoogleMapsAPI() {
      // Check if Google Maps API is already loaded
      if (window.google) {
        return;
      }

      // Create script element for Google Maps API
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyC6l2P7xCZQOSH1ER0rsWDP6pSVQY5JWlw&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      
      // Set up global callback
      window.initGoogleMaps = () => {
        console.log('Google Maps API loaded');
        this.initializeAutocomplete();
      };
      
      document.head.appendChild(script);
    }
  }
}
</script>

<style scoped>
.create-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #15202b;
  min-height: 100vh;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
  color: #d9d9d9;
}

.page-header h1 {
  color: #1da1f2;
  font-size: 2.5em;
  margin-bottom: 10px;
}

.page-description {
  color: #8899a6;
  font-size: 1.1em;
  margin-bottom: 20px;
}

.create-form-container {
  max-width: 800px;
  margin: 0 auto 30px;
}

.create-form {
  background-color: #192734;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.create-form h3 {
  color: #1da1f2;
  margin-bottom: 20px;
  text-align: center;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  color: #d9d9d9;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 12px;
  border: 1px solid #38444d;
  border-radius: 10px;
  background-color: #15202b;
  color: #d9d9d9;
  font-size: 16px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #1da1f2;
}

.form-textarea {
  resize: vertical;
}

.input-help {
  color: #8899a6;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 30px;
}

.submit-button {
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.submit-button:hover:not(:disabled) {
  background-color: #1a91da;
}

.submit-button:disabled {
  background-color: #2a3f54;
  cursor: not-allowed;
}

.cancel-button {
  background-color: transparent;
  color: #8899a6;
  border: 1px solid #2a3f54;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-button:hover {
  background-color: #2a3f54;
  color: #d9d9d9;
}

/* File Upload Styling */
.file-upload-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.file-input {
  display: none;
}

.file-upload-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: #1da1f2;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s ease;
  text-decoration: none;
}

.file-upload-button:hover {
  background-color: #1a91da;
}

.file-name {
  color: #d9d9d9;
  font-size: 14px;
  font-style: italic;
}

.image-preview-container {
  position: relative;
  display: inline-block;
  margin-top: 12px;
}

.image-preview {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #2a3f54;
}

.remove-image-button {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #e74c3c;
  color: #ffffff;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background-color 0.3s ease;
}

.remove-image-button:hover {
  background-color: #c0392b;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }
  
  .create-form {
    padding: 20px;
  }
}

@media (max-width: 600px) {
  .create-form-container {
    margin: 0 auto 30px;
    padding: 0 4px;
  }
  
  .create-form {
    padding: 15px;
  }
  
  .form-input,
  .form-textarea,
  .form-select {
    box-sizing: border-box;
    width: 100%;
    font-size: 16px; /* Prevents zoom on iOS */
  }
  
  .form-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .submit-button,
  .cancel-button {
    width: 100%;
  }
}
</style> 